import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Bike, MapPin, Wallet, PackageCheck, Camera, ShieldAlert,
  Navigation2, PhoneCall, RefreshCw, Timer, IndianRupee, ArrowRight, Play, Pause,
  ScanLine, CircleAlert, BadgeCheck, ScrollText, Award, Snowflake,
} from 'lucide-react';
import { useAuth } from '../lib/auth';
import { fleet } from '../lib/api';
import SpeedBonusTimer from '../components/SpeedBonusTimer';
import AuthImg from '../components/AuthImg';

const VEHICLES = [
  { id: 'bicycle',    label: 'Bicycle' },
  { id: 'scooter',    label: 'Scooter' },
  { id: 'motorcycle', label: 'Motorcycle' },
  { id: 'auto',       label: 'Auto' },
  { id: 'ev-scooter', label: 'EV Scooter' },
  { id: 'car',        label: 'Car' },
];

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

async function getCoords() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      p => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 8000 },
    );
  });
}

export default function Fleet() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState('board');

  const load = useCallback(async () => {
    if (!user) return;
    const r = await fleet.me();
    setMe(r);
  }, [user]);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav('/'); return; }
    load();
  }, [user, loading, nav, load]);

  if (loading) return <div className="max-w-5xl mx-auto px-6 py-16 text-ink/60">Loading…</div>;
  if (!user) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#D35400]">Delivery Fleet</div>
          <h1 className="font-heading font-black text-3xl md:text-4xl mt-1">Hyper-local rider dashboard</h1>
          <p className="text-sm text-ink/60 mt-1">
            Ride, pickup and hand over — every step signed with a photo, an OTP and a live GPS check.
          </p>
        </div>
        {me?.registered && (
          <div className="flex items-center gap-2 flex-wrap">
            {me.partner?.driver_id && (
              <span data-testid="fleet-driver-id"
                className="inline-flex items-center gap-1 pill !bg-[#1B5E20]/10 !text-[#1B5E20] !border-[#1B5E20]/20 font-mono">
                <BadgeCheck className="w-3 h-3"/>{me.partner.driver_id}
              </span>
            )}
            {me.partner?.tier && (
              <span className={`pill !bg-white ${me.partner.tier === 'senior' ? '!text-[#7A4A00] !border-[#F1C40F]/60' : '!text-ink/60'}`}>
                <Award className="w-3 h-3"/>{me.partner.tier === 'senior' ? 'Senior' : 'Junior'}
              </span>
            )}
            {me.partner?.frozen && (
              <span className="pill !bg-[#0D47A1]/10 !text-[#0D47A1] !border-[#0D47A1]/30">
                <Snowflake className="w-3 h-3"/>Frozen
              </span>
            )}
            <PartnerStatusPill partner={me.partner} onChanged={load}/>
          </div>
        )}
      </div>

      {!me?.registered
        ? <RegisterPartner onDone={load}/>
        : (
          <>
            <TabBar tab={tab} setTab={setTab} suspended={me.partner?.suspended}/>
            {tab === 'board'   && <DispatchBoard/>}
            {tab === 'active'  && <ActiveTrips partner={me.partner}/>}
            {tab === 'wallet'  && <WalletTab/>}
          </>
        )
      }
    </div>
  );
}

/* ------------------------------------------------------------ Register */
function RegisterPartner({ onDone }) {
  const [step, setStep] = useState(1);   // 1: details/voucher · 2: docs
  const [form, setForm] = useState({
    name: '', vehicle_type: 'scooter', license_number: '', home_pincode: '',
    voucher_ms_id: '', aadhaar_number: '', pan_number: '',
  });
  const [files, setFiles] = useState({
    license_photo: null, aadhaar_photo: null, pan_photo: null,
    bond_document: null, voucher_document: null,
  });
  const [busy, setBusy] = useState(false);
  const [assignedId, setAssignedId] = useState(null);

  const setFile = (k, f) => setFiles(prev => ({ ...prev, [k]: f }));

  const downloadTemplate = async (kind) => {
    try {
      const dl = kind === 'bond'
        ? await fleet.downloadBondTemplate(form.voucher_ms_id || null)
        : await fleet.downloadVoucherTemplate(form.voucher_ms_id || null);
      const url = URL.createObjectURL(dl.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = kind === 'bond' ? 'BharatCart-Security-Bond.pdf' : 'BharatCart-Merchant-Voucher.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Could not download template');
    }
  };

  const submit = async () => {
    // Step 1 validation
    if (!form.name || !form.license_number || !form.home_pincode || !form.voucher_ms_id) {
      toast.error('Fill name, licence #, pincode and voucher MS-ID'); return;
    }
    if (!/^\d{12}$/.test(form.aadhaar_number)) { toast.error('Aadhaar must be 12 digits'); return; }
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/i.test(form.pan_number)) { toast.error('PAN format looks off (ABCDE1234F)'); return; }
    const missing = Object.entries(files).filter(([, f]) => !f).map(([k]) => k);
    if (missing.length) {
      toast.error('Attach all documents: ' + missing.map(m => m.replace(/_/g, ' ')).join(', '));
      return;
    }
    try {
      setBusy(true);
      const c = await getCoords();
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, k === 'pan_number' ? String(v).toUpperCase() : v));
      if (c) { fd.append('lat', String(c.lat)); fd.append('lng', String(c.lng)); }
      Object.entries(files).forEach(([k, f]) => fd.append(k, f));
      const r = await fleet.register(fd);
      setAssignedId(r.driver_id || r.partner?.driver_id);
      toast.success('Welcome to the fleet!');
      // Give user a moment to note their driver ID
      setTimeout(onDone, 2500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not register');
    } finally { setBusy(false); }
  };

  if (assignedId) {
    return (
      <section className="mt-8 max-w-2xl card-soft p-8 text-center" data-testid="fleet-registered-card">
        <BadgeCheck className="w-12 h-12 mx-auto text-[#1B5E20]"/>
        <div className="font-heading font-black text-2xl mt-3">Welcome, partner!</div>
        <div className="text-sm text-ink/60 mt-1">Your driver ID has been issued. Keep it safe.</div>
        <div className="mt-6 inline-block bg-[#FFF8EE] border-2 border-[#E67E22] rounded-xl px-6 py-3">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#D35400]">Driver ID</div>
          <div className="font-mono text-3xl font-black tracking-[0.15em] text-[#1B5E20] mt-1">{assignedId}</div>
        </div>
      </section>
    );
  }

  return (
    <section className="mt-8 max-w-2xl card-soft p-6" data-testid="fleet-register-card">
      <div className="font-heading font-black text-2xl">Join as a Delivery Partner</div>
      <p className="text-sm text-ink/60 mt-1">
        Deliver kirana &amp; medicines around your village. <b>₹20 base + ₹5/km</b>, plus a
        <b> speed bonus</b> and <b>refundable ₹1,000 security bond</b>.
      </p>

      {step === 1 ? (
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-2">
          <input data-testid="fleet-name" placeholder="Your full name (as on Aadhaar)"
            value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
            className="md:col-span-2 rounded-lg border border-border bg-white px-3 py-2 text-sm"/>
          <select data-testid="fleet-vehicle" value={form.vehicle_type}
            onChange={e => setForm({ ...form, vehicle_type: e.target.value })}
            className="rounded-lg border border-border bg-white px-3 py-2 text-sm">
            {VEHICLES.map(v => <option key={v.id} value={v.id}>{v.label}</option>)}
          </select>
          <input data-testid="fleet-pincode" placeholder="Home pincode"
            value={form.home_pincode} onChange={e => setForm({ ...form, home_pincode: e.target.value })}
            className="rounded-lg border border-border bg-white px-3 py-2 text-sm"/>
          <input data-testid="fleet-license" placeholder="Driving License number"
            value={form.license_number} onChange={e => setForm({ ...form, license_number: e.target.value })}
            className="rounded-lg border border-border bg-white px-3 py-2 text-sm font-mono"/>
          <input data-testid="fleet-aadhaar" placeholder="Aadhaar (12 digits)"
            maxLength={12} value={form.aadhaar_number}
            onChange={e => setForm({ ...form, aadhaar_number: e.target.value.replace(/\D/g, '') })}
            className="rounded-lg border border-border bg-white px-3 py-2 text-sm font-mono"/>
          <input data-testid="fleet-pan" placeholder="PAN (ABCDE1234F)"
            maxLength={10} value={form.pan_number}
            onChange={e => setForm({ ...form, pan_number: e.target.value.toUpperCase() })}
            className="rounded-lg border border-border bg-white px-3 py-2 text-sm font-mono"/>
          <div className="md:col-span-2 rounded-lg border border-[#E67E22]/30 bg-[#FFF8EE] p-3">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#D35400]">Voucher</div>
            <div className="text-xs text-ink/60 mt-1">
              Every new partner must be vouched for by an existing merchant on BharatCart. Enter their <b>MS-ID</b>.
            </div>
            <input data-testid="fleet-voucher-ms-id" placeholder="Merchant MS-ID (MS12345678)"
              value={form.voucher_ms_id} maxLength={10}
              onChange={e => setForm({ ...form, voucher_ms_id: e.target.value.toUpperCase() })}
              className="mt-2 w-full rounded-lg border border-border bg-white px-3 py-2 text-sm font-mono"/>
          </div>
          <button data-testid="fleet-step2-btn" onClick={() => setStep(2)}
            className="btn-primary md:col-span-2">
            Continue to documents <ArrowRight className="w-4 h-4"/>
          </button>
        </div>
      ) : (
        <div className="mt-5 space-y-4" data-testid="fleet-docs-step">
          <div className="rounded-lg border border-[#0D47A1]/20 bg-[#E3F2FD]/40 p-3">
            <div className="text-xs uppercase tracking-wider font-bold text-[#0D47A1]">Download &amp; sign — then upload</div>
            <div className="flex flex-wrap gap-2 mt-2">
              <button data-testid="fleet-dl-bond" onClick={() => downloadTemplate('bond')}
                type="button" className="pill !bg-white hover:border-[#0D47A1] hover:text-[#0D47A1]">
                <ScrollText className="w-3 h-3"/>Bond template PDF
              </button>
              <button data-testid="fleet-dl-voucher" onClick={() => downloadTemplate('voucher')}
                type="button" className="pill !bg-white hover:border-[#0D47A1] hover:text-[#0D47A1]">
                <ScrollText className="w-3 h-3"/>Voucher letter PDF
              </button>
            </div>
          </div>

          <DocUpload testId="fleet-file-license" label="Driving License photo"
            hint="Front side, clear. JPG / PNG / PDF."
            file={files.license_photo} setFile={f => setFile('license_photo', f)}
            accept="image/jpeg,image/png,image/webp,application/pdf"/>
          <DocUpload testId="fleet-file-aadhaar" label="Aadhaar photo"
            hint="Front side (mask centre 8 digits if you prefer)."
            file={files.aadhaar_photo} setFile={f => setFile('aadhaar_photo', f)}
            accept="image/jpeg,image/png,image/webp"/>
          <DocUpload testId="fleet-file-pan" label="PAN card photo"
            hint="Full PAN visible."
            file={files.pan_photo} setFile={f => setFile('pan_photo', f)}
            accept="image/jpeg,image/png,image/webp"/>
          <DocUpload testId="fleet-file-bond" label="Signed Security Bond"
            hint="Download the template above, sign, then upload."
            file={files.bond_document} setFile={f => setFile('bond_document', f)}
            accept="image/jpeg,image/png,image/webp,application/pdf"/>
          <DocUpload testId="fleet-file-voucher" label="Signed Merchant Voucher letter"
            hint="Have the vouching merchant sign the voucher PDF, then upload."
            file={files.voucher_document} setFile={f => setFile('voucher_document', f)}
            accept="image/jpeg,image/png,image/webp,application/pdf"/>

          <div className="flex justify-between gap-2 pt-2">
            <button onClick={() => setStep(1)} className="pill !bg-white">Back</button>
            <button data-testid="fleet-register-submit" disabled={busy} onClick={submit}
              className="btn-primary">
              <Bike className="w-4 h-4"/>{busy ? 'Registering…' : 'Register — get my Driver ID'}
            </button>
          </div>
          <div className="text-[10px] text-ink/40 uppercase tracking-wider">
            Your credentials are stored securely in the Emergent Object Store. Only admins can view them.
          </div>
        </div>
      )}
    </section>
  );
}

function DocUpload({ testId, label, hint, file, setFile, accept }) {
  return (
    <label className={`block rounded-lg border-2 border-dashed p-3 cursor-pointer text-sm transition
      ${file ? 'border-[#1B5E20] bg-[#E8F5E9]/40' : 'border-border bg-white hover:border-[#E67E22]'}`}>
      <div className="font-semibold">{label}</div>
      <div className="text-xs text-ink/60 mt-0.5">
        {file ? `Selected · ${file.name}` : hint}
      </div>
      <input data-testid={testId} type="file" accept={accept} className="hidden"
        onChange={e => setFile(e.target.files?.[0] || null)}/>
    </label>
  );
}

/* ------------------------------------------------------------ Status pill */
function PartnerStatusPill({ partner, onChanged }) {
  const [busy, setBusy] = useState(false);
  const active = !!partner.active && !partner.suspended;
  const toggle = async () => {
    setBusy(true);
    try {
      const c = await getCoords();
      await fleet.updateLocation({
        lat: c?.lat ?? partner.lat ?? 0,
        lng: c?.lng ?? partner.lng ?? 0,
        active: !active,
      });
      toast.success(!active ? 'You are online' : 'You are offline');
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update');
    } finally { setBusy(false); }
  };
  if (partner.suspended) {
    return (
      <span data-testid="fleet-suspended-pill"
        className="pill !bg-[#FFEBEE] !text-[#C62828] !border-[#FFCDD2]">
        <ShieldAlert className="w-3 h-3"/>Suspended · {partner.suspended_reason || 'contact admin'}
      </span>
    );
  }
  return (
    <button data-testid="fleet-online-toggle" disabled={busy} onClick={toggle}
      className={`pill !bg-white ${active ? 'border-[#1B5E20] text-[#1B5E20]' : 'text-ink/60'} hover:border-[#E67E22] hover:text-[#D35400]`}>
      {active ? <Play className="w-3 h-3"/> : <Pause className="w-3 h-3"/>}
      {active ? 'Online' : 'Offline'}
    </button>
  );
}

/* ------------------------------------------------------------ Tab bar */
function TabBar({ tab, setTab, suspended }) {
  const items = [
    { id: 'board',  label: 'Dispatch board', Icon: MapPin },
    { id: 'active', label: 'Active & recent', Icon: PackageCheck },
    { id: 'wallet', label: 'Wallet & COD', Icon: Wallet },
  ];
  return (
    <div className="mt-6 flex gap-2 flex-wrap border-b border-border">
      {items.map(({ id, label, Icon }) => (
        <button key={id} data-testid={`fleet-tab-${id}`} onClick={() => setTab(id)}
          disabled={suspended}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-t-md text-sm font-semibold border-b-2 transition ${
            tab === id ? 'border-[#E67E22] text-[#D35400] bg-[#FFF8EE]' : 'border-transparent text-ink/60 hover:text-[#D35400]'
          } ${suspended ? 'opacity-50 cursor-not-allowed' : ''}`}>
          <Icon className="w-4 h-4"/>{label}
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------ Dispatch board */
function DispatchBoard() {
  const [rows, setRows] = useState([]);
  const [radius, setRadius] = useState(5);
  const [busy, setBusy] = useState(true);
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const c = await getCoords();
      if (c) {
        try { await fleet.updateLocation({ lat: c.lat, lng: c.lng, active: true }); } catch {}
      }
      const r = await fleet.availableTasks(radius);
      setRows(r.tasks || []);
      setNote(r.note || '');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load dispatch board');
    } finally { setBusy(false); }
  }, [radius]);

  useEffect(() => {
    load();
    const t = setInterval(load, 20_000);
    return () => clearInterval(t);
  }, [load]);

  const accept = async (id) => {
    try {
      await fleet.accept(id);
      toast.success('Task accepted — head to the shop!');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not accept');
    }
  };

  return (
    <div className="mt-6">
      <div className="flex items-center gap-3 flex-wrap mb-3">
        <label className="inline-flex items-center gap-2 text-xs font-semibold text-ink/70">
          Radius
          <select data-testid="fleet-radius" value={radius} onChange={e => setRadius(parseInt(e.target.value))}
            className="rounded-lg border border-border bg-white px-2 py-1 text-sm">
            <option value={2}>2 km</option><option value={5}>5 km</option>
            <option value={10}>10 km</option><option value={20}>20 km</option>
          </select>
        </label>
        <button data-testid="fleet-refresh-board" onClick={load}
          className="pill !bg-white hover:border-[#E67E22] hover:text-[#D35400]">
          <RefreshCw className="w-3 h-3"/>Refresh
        </button>
        <div className="text-xs text-ink/50 ml-auto">Refreshes every 20s</div>
      </div>

      {busy && <div className="text-sm text-ink/60">Scanning nearby pickups…</div>}
      {!busy && rows.length === 0 && (
        <div className="card-soft p-6 text-sm text-ink/60">
          {note || 'No pickups nearby right now. Stay online — new tasks appear here automatically.'}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-3">
        {rows.map(t => (
          <div key={t.id} data-testid={`fleet-task-${t.id}`}
            className="card-soft p-4 border-l-4 border-[#E67E22]">
            <div className="flex justify-between gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-[#D35400]">
                  {t.distance_from_me_km?.toFixed(1)} km away · {t.distance_km?.toFixed(1)} km trip
                </div>
                <div className="font-heading font-bold text-lg mt-0.5">{t.store_name || 'Local shop'}</div>
                <div className="text-xs text-ink/60 truncate">{t.pickup_address}</div>
              </div>
              <div className="text-right">
                <div className="inline-flex items-center gap-1 font-heading font-black text-xl text-[#1B5E20]">
                  <IndianRupee className="w-4 h-4"/>{t.estimated_earning_inr}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-ink/50">est. earning</div>
              </div>
            </div>
            <div className="text-xs text-ink/50 mt-2">→ {t.drop_address || 'Customer address'}</div>
            <button data-testid={`fleet-accept-${t.id}`} onClick={() => accept(t.id)}
              className="btn-primary mt-3 w-full">
              Accept trip <ArrowRight className="w-4 h-4"/>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ Active + recent */
function ActiveTrips({ partner }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await fleet.myTasks();
      setRows(r.tasks || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load your trips');
    } finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (busy) return <div className="mt-6 text-sm text-ink/60">Loading…</div>;
  if (rows.length === 0) return (
    <div className="mt-6 card-soft p-6 text-sm text-ink/60">
      No trips yet. Accept a task from the Dispatch board.
    </div>
  );

  return (
    <div className="mt-6 space-y-3">
      {rows.map(t => <TripCard key={t.id} task={t} onChanged={load} partner={partner}/>)}
    </div>
  );
}

function TripCard({ task, onChanged, partner }) {
  const [busy, setBusy] = useState(false);
  const pickupFileRef = useRef(null);
  const dropFileRef = useRef(null);
  const [otp, setOtp] = useState('');
  const [isCod, setIsCod] = useState(false);
  const [codAmount, setCodAmount] = useState('');

  const doPickup = async () => {
    const f = pickupFileRef.current?.files?.[0];
    if (!f) { toast.error('Take a photo of the sealed package first.'); return; }
    try {
      setBusy(true);
      const c = await getCoords();
      const fd = new FormData();
      fd.append('pickup_photo', f);
      if (c) { fd.append('lat', String(c.lat)); fd.append('lng', String(c.lng)); }
      await fleet.pickup(task.id, fd);
      toast.success('Package picked up — deliver safely!');
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not mark pickup');
    } finally { setBusy(false); }
  };

  const doDeliveryPhoto = async () => {
    const f = dropFileRef.current?.files?.[0];
    if (!f) { toast.error('Take a photo at the doorstep first.'); return; }
    try {
      setBusy(true);
      const c = await getCoords();
      const fd = new FormData();
      fd.append('delivery_photo', f);
      if (c) { fd.append('lat', String(c.lat)); fd.append('lng', String(c.lng)); }
      await fleet.deliveryPhoto(task.id, fd);
      toast.success('Delivery photo saved — now ask for the OTP.');
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save delivery photo');
    } finally { setBusy(false); }
  };

  const doComplete = async () => {
    if (!/^\d{4,6}$/.test(otp)) { toast.error('Enter the 4-digit OTP from the customer'); return; }
    try {
      setBusy(true);
      const c = await getCoords();
      const body = {
        otp,
        lat: c?.lat ?? null,
        lng: c?.lng ?? null,
        is_cod: isCod,
        cod_amount_inr: isCod ? parseFloat(codAmount) || 0 : null,
      };
      const r = await fleet.complete(task.id, body);
      const bonusMsg = r.earning?.speed_bonus_paid ? ' · ⚡ speed bonus applied' : '';
      toast.success(`Delivered · earned ₹${r.earning?.total}${bonusMsg}`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not complete');
    } finally { setBusy(false); }
  };

  const status = task.status;
  const statusMeta = {
    dispatched: { label: 'Awaiting pickup', tone: 'bg-[#FFF8E1] text-[#7A4A00]' },
    accepted:   { label: 'Head to shop',    tone: 'bg-[#E3F2FD] text-[#0D47A1]' },
    picked_up:  { label: 'On the way',      tone: 'bg-[#FFF3E0] text-[#D35400]' },
    completed:  { label: 'Delivered',        tone: 'bg-[#E8F5E9] text-[#1B5E20]' },
    cancelled:  { label: 'Cancelled',        tone: 'bg-[#F5F5F5] text-[#616161]' },
  }[status] || { label: status, tone: 'bg-[#F5F5F5] text-[#616161]' };

  const mapUrl = (task.drop_lat && task.drop_lng)
    ? `https://www.google.com/maps/dir/?api=1&destination=${task.drop_lat},${task.drop_lng}`
    : null;
  const pickupMapUrl = (task.pickup_lat && task.pickup_lng)
    ? `https://www.google.com/maps/dir/?api=1&destination=${task.pickup_lat},${task.pickup_lng}`
    : null;

  return (
    <div className="card-soft p-4" data-testid={`fleet-trip-${task.id}`}>
      <div className="flex justify-between gap-2 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-[#D35400]">
            {task.distance_km?.toFixed(1)} km · earn ₹{task.estimated_earning_inr}
          </div>
          <div className="font-heading font-bold text-lg mt-0.5">{task.store_name || 'Shop'}</div>
          <div className="text-xs text-ink/60">Pickup: {task.pickup_address}</div>
          {status !== 'dispatched' && (
            <div className="text-xs text-ink/60 mt-1">
              Drop: {task.drop_address || 'Customer address'}
              {task.customer_phone && (
                <a href={`tel:${task.customer_phone}`} data-testid={`fleet-call-${task.id}`}
                  className="ml-2 inline-flex items-center gap-1 text-[#1B5E20] font-semibold">
                  <PhoneCall className="w-3 h-3"/>{task.customer_phone}
                </a>
              )}
            </div>
          )}
        </div>
        <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded ${statusMeta.tone}`}>
          {statusMeta.label}
        </span>
      </div>

      {/* Step-by-step actions */}
      {status === 'accepted' && (
        <div className="mt-3 rounded-lg border border-[#E67E22]/25 bg-[#FFF8EE] p-3">
          <div className="text-xs uppercase tracking-wider font-bold text-[#D35400]">Step 1 · Pickup</div>
          <div className="text-xs text-ink/60 mt-1">Photograph the sealed package at the shop.</div>
          <div className="mt-2 flex flex-wrap gap-2 items-center">
            {pickupMapUrl && (
              <a href={pickupMapUrl} target="_blank" rel="noreferrer"
                data-testid={`fleet-nav-shop-${task.id}`}
                className="pill !bg-white hover:border-[#E67E22] hover:text-[#D35400]">
                <Navigation2 className="w-3 h-3"/>Directions to shop
              </a>
            )}
            <label className="pill !bg-white hover:border-[#E67E22] hover:text-[#D35400] cursor-pointer">
              <Camera className="w-3 h-3"/>Capture pickup photo
              <input ref={pickupFileRef} data-testid={`fleet-pickup-file-${task.id}`}
                type="file" accept="image/*" capture="environment" className="hidden"
                onChange={() => {}}/>
            </label>
            <button data-testid={`fleet-pickup-btn-${task.id}`} onClick={doPickup} disabled={busy}
              className="btn-primary ml-auto">
              <PackageCheck className="w-4 h-4"/>Mark picked up
            </button>
          </div>
        </div>
      )}

      {status === 'picked_up' && (
        <>
          {task.pickup_photo_url && (
            <span className="inline-block mt-2">
              <AuthImg src={task.pickup_photo_url} alt="Pickup proof" data-testid={`fleet-pickup-proof-${task.id}`}
                className="max-h-24 rounded-lg border border-border object-cover"/>
            </span>
          )}
          <div className="mt-3 rounded-lg border border-[#0D47A1]/20 bg-[#E3F2FD]/40 p-3">
            <div className="text-xs uppercase tracking-wider font-bold text-[#0D47A1]">Step 2 · Delivery photo</div>
            <div className="text-xs text-ink/60 mt-1">Snap the parcel at the doorstep before entering OTP.</div>
            <div className="mt-2 flex flex-wrap gap-2 items-center">
              {mapUrl && (
                <a href={mapUrl} target="_blank" rel="noreferrer"
                  data-testid={`fleet-nav-drop-${task.id}`}
                  className="pill !bg-white hover:border-[#0D47A1] hover:text-[#0D47A1]">
                  <Navigation2 className="w-3 h-3"/>Directions to customer
                </a>
              )}
              <label className="pill !bg-white hover:border-[#0D47A1] hover:text-[#0D47A1] cursor-pointer">
                <Camera className="w-3 h-3"/>{task.delivery_photo_url ? 'Retake delivery photo' : 'Capture delivery photo'}
                <input ref={dropFileRef} data-testid={`fleet-drop-file-${task.id}`}
                  type="file" accept="image/*" capture="environment" className="hidden"
                  onChange={() => {}}/>
              </label>
              <button data-testid={`fleet-drop-photo-btn-${task.id}`} onClick={doDeliveryPhoto} disabled={busy}
                className="pill !bg-[#0D47A1] !text-white hover:bg-[#0D47A1]/90 ml-auto">
                Save delivery photo
              </button>
            </div>
            {task.delivery_photo_url && (
              <a href={task.delivery_photo_url} target="_blank" rel="noreferrer" className="inline-block mt-2">
                <img src={task.delivery_photo_url} alt="Delivery proof"
                  className="max-h-24 rounded-lg border border-border object-cover"/>
              </a>
            )}
          </div>

          <SpeedBonusTimer task={task}/>
          <div className="mt-3 rounded-lg border border-[#1B5E20]/20 bg-[#E8F5E9]/40 p-3">
            <div className="text-xs uppercase tracking-wider font-bold text-[#1B5E20]">Step 3 · Handover OTP + location</div>
            <div className="text-xs text-ink/60 mt-1">
              Get within <b>100 m</b> of the customer, ask for the 4-digit OTP shown on their app.
            </div>
            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
              <input data-testid={`fleet-otp-${task.id}`}
                inputMode="numeric" placeholder="Enter OTP" maxLength={6}
                value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
                className="rounded-lg border border-border bg-white px-3 py-2 text-lg font-mono tracking-widest text-center"/>
              <label className="inline-flex items-center gap-2 text-sm rounded-lg border border-border bg-white px-3 py-2 cursor-pointer">
                <input data-testid={`fleet-cod-toggle-${task.id}`}
                  type="checkbox" checked={isCod} onChange={e => setIsCod(e.target.checked)}/>
                Cash on Delivery
              </label>
              {isCod && (
                <input data-testid={`fleet-cod-amount-${task.id}`}
                  type="number" placeholder="Cash collected ₹" value={codAmount}
                  onChange={e => setCodAmount(e.target.value)}
                  className="md:col-span-2 rounded-lg border border-border bg-white px-3 py-2 text-sm"/>
              )}
              <button data-testid={`fleet-complete-${task.id}`} onClick={doComplete} disabled={busy}
                className="btn-primary md:col-span-2">
                <BadgeCheck className="w-4 h-4"/>{busy ? 'Verifying…' : 'Complete delivery'}
              </button>
            </div>
          </div>
        </>
      )}

      {status === 'completed' && (
        <div className="mt-3 rounded-lg border border-[#1B5E20]/25 bg-[#E8F5E9]/40 p-3 text-sm">
          <div className="text-[10px] uppercase tracking-wider font-bold text-[#1B5E20]">Delivered</div>
          <div>Earned <b>₹{task.earning_inr}</b>{task.speed_bonus_paid && <span className="ml-2 text-[#D35400]">⚡ speed bonus</span>}</div>
          {task.cod_collected_inr > 0 && (
            <div className="text-xs text-ink/60 mt-1">
              COD collected ₹{task.cod_collected_inr} — deposit at office to release from wallet.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------ Wallet */
function WalletTab() {
  const [w, setW] = useState(null);
  const [pending, setPending] = useState({ items: [] });
  const [busy, setBusy] = useState(true);
  const [depAmount, setDepAmount] = useState('');
  const [depMethod, setDepMethod] = useState('upi');
  const [depRef, setDepRef] = useState('');

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [a, b] = await Promise.all([fleet.wallet(), fleet.codPending()]);
      setW(a); setPending(b);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load wallet');
    } finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const deposit = async () => {
    const amt = parseFloat(depAmount);
    if (!amt || amt <= 0) { toast.error('Enter deposit amount'); return; }
    try {
      const r = await fleet.codDeposit({ amount_inr: amt, method: depMethod, reference: depRef || null });
      toast.success(`Cleared ₹${r.cleared_inr} of pending COD`);
      setDepAmount(''); setDepRef('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not record deposit');
    }
  };

  if (busy || !w) return <div className="mt-6 text-sm text-ink/60">Loading wallet…</div>;

  return (
    <div className="mt-6 grid gap-6 lg:grid-cols-[1.2fr_1fr]">
      <section className="card-soft p-5" data-testid="fleet-wallet-summary">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#D35400]">Withdrawable balance</div>
        <div className="font-heading font-black text-5xl mt-1 text-[#1B5E20] tabular-nums">
          {money(w.withdrawable_inr)}
        </div>
        <div className="text-xs text-ink/60 mt-1">
          Lifetime earnings {money(w.lifetime.total_inr)} · {w.lifetime.trips} trips
          {w.cod_outstanding_inr > 0 && (
            <span className="ml-2 text-[#C62828]">· COD holding {money(w.cod_outstanding_inr)}</span>
          )}
        </div>
        <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Base fare" value={money(w.lifetime.base_inr)}/>
          <Stat label="Speed bonus" value={money(w.lifetime.bonus_inr)}/>
          <Stat label="Security bond" value={money(w.security_bond_inr)}
            tone="text-[#0D47A1]"/>
          <Stat label="COD outstanding" value={money(w.cod_outstanding_inr)}
            tone={w.cod_outstanding_inr > 0 ? 'text-[#C62828]' : ''}/>
        </div>
        <div className="mt-4 rounded-lg border border-[#0D47A1]/20 bg-[#E3F2FD]/40 p-3 text-xs">
          <div className="font-semibold text-[#0D47A1]">Tier · {w.tier === 'senior' ? 'Senior' : 'Junior'}</div>
          {w.tier === 'junior'
            ? <div className="text-ink/70 mt-1">
                {w.trips_to_senior} more clean deliver{w.trips_to_senior === 1 ? 'y' : 'ies'} to become <b>Senior</b> and unlock high-value orders above ₹{w.tier_max_order_inr}.
              </div>
            : <div className="text-ink/70 mt-1">Any order value. Keep your rating above 3.5★.</div>
          }
        </div>
        <div className="text-[11px] text-ink/50 mt-4 uppercase tracking-wider">
          Formula: {w.rules.formula} · {w.rules.speed_bonus}
        </div>
        <div className="mt-1 text-[11px] text-ink/60"><CircleAlert className="w-3 h-3 inline mr-1"/>{w.rules.cod_note}</div>
        <div className="mt-1 text-[11px] text-ink/60"><CircleAlert className="w-3 h-3 inline mr-1"/>{w.rules.security_bond_note}</div>
      </section>

      <section className="card-soft p-5" data-testid="fleet-cod-panel">
        <div className="font-heading font-bold text-lg flex items-center gap-2">
          <IndianRupee className="w-5 h-5"/>COD deposit
        </div>
        <p className="text-xs text-ink/60 mt-1">
          Cash you collected from customers is held against your wallet balance until you deposit it.
        </p>
        <div className="mt-3 grid gap-2">
          <input data-testid="fleet-cod-deposit-amount" type="number" placeholder="Amount to deposit ₹"
            value={depAmount} onChange={e => setDepAmount(e.target.value)}
            className="rounded-lg border border-border bg-white px-3 py-2 text-sm"/>
          <select data-testid="fleet-cod-deposit-method" value={depMethod} onChange={e => setDepMethod(e.target.value)}
            className="rounded-lg border border-border bg-white px-3 py-2 text-sm">
            <option value="upi">UPI</option>
            <option value="cash">Cash at office</option>
            <option value="bank_transfer">Bank transfer</option>
          </select>
          <input data-testid="fleet-cod-deposit-ref" placeholder="UTR / receipt / initials (optional)"
            value={depRef} onChange={e => setDepRef(e.target.value)}
            className="rounded-lg border border-border bg-white px-3 py-2 text-sm font-mono"/>
          <button data-testid="fleet-cod-deposit-btn" onClick={deposit}
            disabled={!depAmount} className="btn-primary">
            <ScanLine className="w-4 h-4"/>Record deposit
          </button>
        </div>

        <div className="mt-5">
          <div className="text-xs uppercase tracking-wider font-bold text-ink/50">Pending COD</div>
          {pending.items.length === 0
            ? <div className="text-sm text-ink/60 mt-1">No outstanding cash — you’re clear!</div>
            : pending.items.map(it => (
                <div key={it.id} data-testid={`fleet-cod-item-${it.id}`}
                  className="flex justify-between items-center py-2 border-b border-border/60 text-sm">
                  <div>
                    <div className="font-mono text-xs text-ink/60">Task {it.task_id?.slice(0,8)}…</div>
                    <div className="text-[11px] text-ink/50">{new Date(it.created_at).toLocaleString()}</div>
                  </div>
                  <div className="font-heading font-bold">{money(it.amount_inr)}</div>
                </div>
              ))
          }
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, tone = '' }) {
  return (
    <div className="rounded-lg border border-border bg-white p-3">
      <div className="text-[10px] uppercase tracking-wider text-ink/50">{label}</div>
      <div className={`font-heading font-bold text-lg tabular-nums ${tone}`}>{value}</div>
    </div>
  );
}
